// PAY · MOVE TO TREASURY · REFILL HOT — the three focused pages (UI rebuild batch 4, custody hand-off; Astra's `pay`,
// `custody-move`, `refill`). One engine (components/v2/custody/useMoveAttempt — MoveSheet.tsx's, copied), three titles.
//
//   /custody/pay/:entryId   a payout to one address-book entry. Offered exactly where Payout addresses offers Pay today
//                           (Whitelist.tsx): Whitelisted, or Approved (the 10 USDT test only). Every other stage is ONE
//                           line and no button — a payout nobody can name offers none. The source choices are by class,
//                           as lib/custody-screen.ts paySources derives them: a customer from Hot; a merchant from
//                           Merchant Hot (the default), Hot or Flagged.
//   /custody/move           Hot → the desk's Treasury wallet on this chain (Balances.tsx's moveToTreasury).
//   /custody/refill         Treasury → Hot (Balances.tsx's refillHot).
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { custody as api, type WalletBalance } from '../../../lib/api';
import { CLASS_WORD, money, shortAddress, walletName } from '../../../lib/custody-format';
import { poolTotal, whitelistGroup, moveDestinationOf } from '../../../lib/custody-screen';
import { StateLine, PageHead, SkeletonRows, ReadFailed, Panel, EmptyState, TAP } from '../../../components/v2/kit';
import { CA } from '../../../components/v2/custody-admin/paths';
import { FormAside } from '../../../components/v2/move/FormFrame';
import { MoveForm } from '../../../components/v2/custody/MoveForm';
import { useMoveAttempt } from '../../../components/v2/custody/useMoveAttempt';
import { GatedAddress, stageWord, clock } from '../../../components/v2/custody/parts';
import { isDesk, payeeKind, payeeName, sourcesFor } from '../../../components/v2/custody/desk';

// A total says how much of it was read (Screens and Astra, 27 Oct 2026, #8): one Hot wallet at 20 and another never read
// is "20 USDT · 1 of 2 read", as v1's Balances says it — never "holds 20 USDT".
const total = (rows: WalletBalance[]) => {
  const t = poolTotal(rows);
  if (t.total === null) return 'Not known';
  return `${money(t.total, rows[0]?.asset ?? 'USDTTRC')}${t.read < t.all ? ` · ${t.read} of ${t.all} read` : ''}`;
};

/** A read the page cannot stand without: waiting or failed, said as that. */
function ReadGate({ title, q, what }: { title: string; q: { isError: boolean; error: unknown; refetch: () => unknown }; what: string }) {
  return (
    <div className="lg:max-w-[600px]">
      <PageHead title={title} />
      {q.isError
        ? <ReadFailed what={what} error={q.error} onRetry={() => void q.refetch()} tone="bad" />
        : <section className="rounded-[10px] border border-stroke bg-panel"><SkeletonRows rows={3} what={what} /></section>}
    </div>
  );
}

/** PAY OUT, FROM CUSTODY'S TOP (his "these things are so easy to miss", 4 Nov 2026: Pay sat under Wallets → payout addresses → the
 *  entry → Pay). The approved payout addresses — the whitelisted first, then those whose 10 USDT test is next — each one tap to
 *  the pay page. A proposed address, one waiting for its test's Received, and a retired one are not offered here. */
export const payChoices = <E extends Parameters<typeof whitelistGroup>[0] & { partyName: string }>(entries: readonly E[]): E[] => entries
  .filter((e) => { const g = whitelistGroup(e); return g === 'payable' || g === 'approved'; })
  .sort((a, b) => (whitelistGroup(a) === 'payable' ? 0 : 1) - (whitelistGroup(b) === 'payable' ? 0 : 1) || a.partyName.localeCompare(b.partyName));

export function PayChooseV2() {
  const q = useQuery({ queryKey: ['custody', 'whitelist'], queryFn: api.whitelist });
  const limits = useQuery({ queryKey: ['custody', 'limits'], queryFn: api.limits, staleTime: 30_000, retry: false });
  if (!q.data) return <ReadGate title="Pay out" q={q} what="the address book" />;
  const rows = payChoices(q.data.entries);
  const test = limits.data?.testPaymentUsdt ?? '10';
  return (
    <div className="lg:max-w-[720px]">
      <PageHead title="Pay out" sub="Approved payout addresses" />
      {q.isError && <StateLine tone="bad" className="mb-4" more="The list shown is the last one read; a stage may have moved.">Couldn&apos;t refresh the address book.</StateLine>}
      {rows.length === 0 ? (
        <Panel><EmptyState action={<Link to={CA.propose} className={TAP}>Propose payout address</Link>}>No approved payout address.</EmptyState></Panel>
      ) : (
        <Panel flush>
          <ul>
            {rows.map((e) => (
              <li key={e.id} className="border-b border-stroke-soft last:border-b-0">
                <Link to={`/custody/pay/${e.id}`} data-pay={e.id} className="flex min-h-[64px] items-center justify-between gap-3 px-4 py-3 hover:bg-panel-2 lg:px-5">
                  <span className="min-w-0">
                    <span className="block break-words text-body font-medium">{payeeName(e)}</span>
                    <span className="mt-0.5 block text-meta text-tg-hint">{stageWord(e.stage) ?? '—'} · {e.chain} · <span className="whitespace-nowrap font-mono">{shortAddress(e.address)}</span></span>
                  </span>
                  <span className="btn-link shrink-0 whitespace-nowrap text-body">{whitelistGroup(e) === 'approved' ? `Send the ${test} USDT test →` : 'Pay →'}</span>
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  );
}

export function PayV2() {
  const { entryId } = useParams();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['custody', 'whitelist'], queryFn: api.whitelist });
  const limits = useQuery({ queryKey: ['custody', 'limits'], queryFn: api.limits, staleTime: 30_000, retry: false });
  const e = q.data?.entries.find((x) => String(x.id) === entryId) ?? null;
  const group = e ? whitelistGroup(e) : null;
  const offered = group === 'payable' || group === 'approved';
  const test = limits.data?.testPaymentUsdt ?? '10';
  const sources = e && offered ? sourcesFor(e) : (['running_hot'] as const);
  const v = useMoveAttempt({
    // Restored and reconciled by the destination's id WHATEVER its stage now (#1): an approved address whose test was
    // recorded moves to test_sent, and the page kept asking after that payment only while a NEW one was allowed. A
    // new payment is still offered only where the stage allows it (the refusal below).
    // A desk wallet is named by the register (N-091), never by the party: the readback and the card say that name.
    destination: e ? { ...moveDestinationOf(e, test)!, name: payeeName(e) } : null,
    sources,
    onRecorded: () => { void qc.invalidateQueries({ queryKey: ['custody', 'whitelist'] }); },
    // A payout to a customer or merchant may name the deal it settles (P-063); a desk wallet's never does.
    withDeal: !!e && !isDesk(e),
  });
  if (!q.data) return <ReadGate title="Pay approved address" q={q} what="the address book" />;

  // Not offered: the stage's own line (Whitelist.tsx), and no button.
  const refusal = !e ? <StateLine tone="bad">Not a payable address — nothing to pay.</StateLine>
    : group === 'awaiting' ? <StateLine tone="warn" more="Approve payout address in the Books; then a 10 USDT test the customer confirms whitelists it.">Awaiting the Books&apos; approval — nothing can be paid.</StateLine>
    : group === 'test_sent' ? <StateLine tone="warn" more="The test is on its way, or landed and waits for your Received on Payout addresses. Nothing else goes until then.">Waiting for the test — press Received first.</StateLine>
    : group === 'unavailable' ? <StateLine tone="bad">Retired — not payable.</StateLine>
    : group === 'unknown' ? <StateLine tone="bad" more="The address book was read without this address's whitelisting stage, so nothing is offered. Refresh, and tell the wallet session if it stays.">Stage not read — nothing can be paid here yet.</StateLine>
    : null;

  return (
    <MoveForm
      title="Pay approved address"
      dealParty={e && !isDesk(e) ? e.partySlug : undefined}
      review="Review payout"
      verb="Pay"
      v={v}
      sources={sources}
      // An attempt still being settled is shown whatever the stage now; only a NEW payment is refused.
      refusal={v.locked ? undefined : refusal}
      notice={q.isError ? <StateLine tone="bad" more="The address book shown is the last one read; its stage may have moved.">Couldn&apos;t refresh the address book.</StateLine> : undefined}
      lead={e ? <>{stageWord(e.stage) ?? 'Stage not read'} · TRC20 · address ending withheld until signing</> : undefined}
      facts={e ? [
        { label: 'Payee', value: <span className="font-medium">{payeeName(e)}</span> },
        { label: 'Destination', value: <GatedAddress value={e.address} /> },
      ] : []}
      aside={e && (
        <FormAside
          title="Destination"
          figure={stageWord(e.stage) ?? '—'}
          sub="Eligibility is checked again before signing."
          facts={[
            ...(payeeKind(e) ? [{ label: 'Payee is', value: payeeKind(e)! }] : []),
            { label: 'Read', value: `${clock(new Date(q.dataUpdatedAt).toISOString())} Dubai` },
          ]}
          help={<p>Hot pays customers, merchants and the desk&apos;s own wallets. Merchant Hot and Flagged pay only parties the Books mark merchant; Flagged releases one at a time.</p>}
        />
      )}
      about="A payout to this approved address. The desk builds the transaction and offers it in Approvals; nothing moves until you sign it on your Ledger, by phone over Bluetooth."
    />
  );
}

/** FLAGGED BACK TO HOT (the wallet's item 5, db/0109, 3 Nov 2026): the desk's own money Hot, Merchant Hot or Treasury moved into
 *  Flagged goes back to custody's Hot. It names no depositor (a depositor's held money leaves only as their payout) and never
 *  more than the desk's figure, which the form shows; the server says the rest (nothing_held, over_held, its own line). */
export function FlaggedToHotV2() {
  const q = useQuery({ queryKey: ['custody', 'balances'], queryFn: api.balances, refetchInterval: 30_000 });
  const wallets = q.data?.wallets ?? [];
  const hot = wallets.filter((w) => w.class === 'running_hot');
  const flagged = wallets.filter((w) => w.class === 'frozen');
  const target = hot[0] ?? null;
  const v = useMoveAttempt({ destination: target ? { id: target.id, name: walletName(target), address: target.address } : null, sources: ['frozen'], deskReturn: true });
  const title = 'Flagged back to Hot';
  if (!q.data) return <ReadGate title={title} q={q} what="the balances" />;
  return (
    <MoveForm
      title={title}
      review="Review transfer"
      verb="Move"
      v={v}
      sources={['frozen']}
      refusal={!target ? <StateLine tone="bad">No Hot on this chain.</StateLine>
        : v.deskHeldRead && v.deskHeld === null && !v.locked ? <StateLine tone="warn">Flagged back to Hot is not switched on yet.</StateLine>
        : v.deskHeld && Number(v.deskHeld.held) <= 0 && !v.locked ? <StateLine>No desk money in Flagged.</StateLine>
        : undefined}
      notice={q.isError ? <StateLine tone="bad" more="The balances shown are the last ones read.">Couldn&apos;t refresh the balances.</StateLine> : undefined}
      facts={target ? [
        { label: 'To', value: <span className="font-medium">{walletName(target)}</span> },
        { label: 'Address', value: <GatedAddress value={target.address} /> },
      ] : []}
      aside={
        <FormAside
          title="Flagged holds"
          figure={flagged.length ? total(flagged) : 'Not known'}
          sub={q.isError ? 'Last reading — the latest read failed.' : q.data.freshness !== 'fresh' ? 'Figures not current.' : undefined}
          facts={[
            ...(v.deskHeld ? [{ label: 'The desk\'s money', value: money(v.deskHeld.held, 'USDTTRC') }] : []),
            ...(q.data.asOf ? [{ label: 'Read', value: `${clock(q.data.asOf)} Dubai` }] : []),
          ]}
        />
      }
      about="The desk's own money in Flagged, back to Hot. A depositor's held money leaves only as their payout. The desk builds the transaction and offers it in Approvals; nothing moves until you sign it there."
    />
  );
}

/** Move to Treasury (Hot → the desk's Treasury wallet) and Refill Hot (Treasury → Hot): one page, two directions. */
function DeskMove({ kind }: { kind: 'move' | 'refill' }) {
  const q = useQuery({ queryKey: ['custody', 'balances'], queryFn: api.balances, refetchInterval: 30_000 });
  const wallets = q.data?.wallets ?? [];
  const hot = wallets.filter((w) => w.class === 'running_hot');
  const treasury = wallets.filter((w) => w.class === 'treasury');
  // Balances.tsx: the vault is the first Treasury wallet on the chain; Hot is the first Hot wallet.
  const target = kind === 'move' ? treasury[0] ?? null : hot[0] ?? null;
  const sources = kind === 'move' ? (['running_hot'] as const) : (['treasury'] as const);
  const v = useMoveAttempt({ destination: target ? { id: target.id, name: walletName(target), address: target.address } : null, sources });
  // HOT TO A DESK WALLET OUTSIDE CUSTODY (the wallet's item 6, his ask, 2 Nov 2026): the ones Hot may pay now are the other
  // destinations of this move, each its own payout page (an approved one sends the test first, as every payout address does).
  const book = useQuery({ queryKey: ['custody', 'whitelist'], queryFn: api.whitelist, enabled: kind === 'move' });
  // Move to Treasury's alone: a cached address book or Flagged read must never put these on Refill (P-059's test found it).
  const deskTo = kind !== 'move' ? [] : (book.data?.entries ?? []).filter((x) => isDesk(x) && ['payable', 'approved'].includes(whitelistGroup(x)));
  // The desk's own money in Flagged, when there is some: its way back is one tap from here (item 5, 3 Nov 2026).
  const flaggedHeld = useQuery({ queryKey: ['custody', 'flagged-held'], queryFn: api.flaggedHeld, enabled: kind === 'move', staleTime: 10_000, retry: false });
  const deskInFlagged = (flaggedHeld.data as { desk?: { held: string } | null } | undefined)?.desk ?? null;
  const backFromFlagged = kind === 'move' && deskInFlagged !== null && Number(deskInFlagged.held) > 0;
  const title = kind === 'move' ? 'Move to Treasury' : `Refill ${CLASS_WORD['running_hot']}`;
  if (!q.data) return <ReadGate title={title} q={q} what="the balances" />;
  const fromRows = kind === 'move' ? hot : treasury;
  return (
    <MoveForm
      title={title}
      review={kind === 'move' ? 'Review transfer' : 'Review refill'}
      verb={kind === 'move' ? 'Move' : 'Refill'}
      v={v}
      sources={sources}
      refusal={target ? undefined : <StateLine tone="bad">No {CLASS_WORD[kind === 'move' ? 'treasury' : 'running_hot']} wallet on this chain.</StateLine>}
      lead={(deskTo.length > 0 || backFromFlagged) && !v.locked ? (
        <span className="block space-y-1">
          {deskTo.length > 0 && (
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>Or to a desk wallet:</span>
              {deskTo.map((x) => <Link key={x.id} to={`/custody/pay/${x.id}`} className="btn-link tap-44 whitespace-nowrap text-meta">{payeeName(x)}</Link>)}
            </span>
          )}
          {backFromFlagged && (
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>Desk money in Flagged: {money(deskInFlagged!.held, 'USDTTRC')}</span>
              <Link to="/custody/return" className="btn-link tap-44 whitespace-nowrap text-meta">Back to Hot</Link>
            </span>
          )}
        </span>
      ) : undefined}
      // Said in the form itself, not only in the aside (hidden on a phone): the figures are the last read.
      notice={q.isError ? <StateLine tone="bad" more="The balances shown are the last ones read.">Couldn&apos;t refresh the balances.</StateLine> : undefined}
      facts={target ? [
        { label: 'To', value: <span className="font-medium">{walletName(target)}</span> },
        { label: 'Address', value: <GatedAddress value={target.address} /> },
      ] : []}
      aside={
        <FormAside
          title={`${CLASS_WORD[sources[0]]} holds`}
          figure={fromRows.length ? total(fromRows) : 'Not known'}
          sub={q.isError ? 'Last reading — the latest read failed.' : q.data.freshness !== 'fresh' ? 'Figures not current.' : undefined}
          facts={[
            ...(kind === 'refill' && hot.length ? [{ label: `${CLASS_WORD['running_hot']} holds`, value: total(hot) }] : []),
            ...(q.data.asOf ? [{ label: 'Read', value: `${clock(q.data.asOf)} Dubai` }] : []),
          ]}
        />
      }
      about={kind === 'move'
        ? 'From Hot to Treasury. The desk builds the transaction and offers it in Approvals as a treasury move; nothing moves until you sign it there.'
        : 'From Treasury to Hot. The desk builds the transaction and offers it in Approvals as a refill; it needs two of the Treasury\'s three keys, and nothing moves until they sign it there.'}
    />
  );
}

export const MoveToTreasuryV2 = () => <DeskMove kind="move" />;
export const RefillV2 = () => <DeskMove kind="refill" />;
